import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Bowtie } from "./icons";
import { trackEvent } from "../../lib/analytics";
import { waitUntilIdle } from "../../lib/popupGate";
import SessionUrgencyTimer from "./SessionUrgencyTimer";

const ZYLVIE_SCRIPT = "https://zylvie.com/bowtiekreative/embed.min.js";
const ZYLVIE_IFRAME = "https://zylvie.com/bowtiekreative/cart/add/D8nk7L4n?disable_backlinks";

export default function ZylvieOfferModal() {
  const [open, setOpen] = useState(false);
  const [iframeHeight, setIframeHeight] = useState(1600); // tall default so nothing is clipped
  const iframeRef = useRef(null);

  // Auto-open after the user has had a chance to engage with the page —
  // never stack on top of higher-priority popups (cookie consent banner).
  // Trigger when EITHER: 15s elapsed since arm, OR the user scrolls past 30%
  // of the viewport — whichever comes first. Cookie consent must be dismissed
  // for either trigger to fire (waitUntilIdle handles that).
  useEffect(() => {
    let cancelWait = () => {};
    let scrollHandler = null;
    let fallbackTimer = null;
    let armed = false;

    const fire = (reason) => {
      if (armed) return;
      armed = true;
      cancelWait = waitUntilIdle("zylvie-offer-modal", () => {
        setOpen(true);
        trackEvent("offer_popup_open", { metadata: { trigger: reason } });
      });
    };

    // 15s timeout fallback so visitors who don't scroll still see the offer.
    fallbackTimer = setTimeout(() => fire("timeout_15s"), 15000);

    // Scroll trigger — fire when user passes 30% of total scrollable height.
    scrollHandler = () => {
      const total = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const pct = window.scrollY / total;
      if (pct >= 0.3) fire("scroll_30pct");
    };
    window.addEventListener("scroll", scrollHandler, { passive: true });

    return () => {
      clearTimeout(fallbackTimer);
      if (scrollHandler) window.removeEventListener("scroll", scrollHandler);
      cancelWait();
    };
  }, []);

  // Inject Zylvie embed script when modal opens
  useEffect(() => {
    if (!open) return;
    if (document.querySelector(`script[src="${ZYLVIE_SCRIPT}"]`)) return;
    const s = document.createElement("script");
    s.src = ZYLVIE_SCRIPT;
    s.async = true;
    document.body.appendChild(s);
  }, [open]);

  // Listen for postMessage from Zylvie iframe to auto-size height (best-effort)
  useEffect(() => {
    const onMessage = (e) => {
      if (!e?.data) return;
      // Common iframe-resizer patterns
      const h =
        (typeof e.data === "object" && (e.data.height || e.data.iframeHeight)) ||
        (typeof e.data === "string" && /^\d+$/.test(e.data) ? parseInt(e.data, 10) : null);
      if (h && h > 400 && h < 5000) setIframeHeight(h);
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  const dismiss = (reason = "manual_dismiss") => {
    trackEvent("offer_popup_dismiss", { metadata: { reason } });
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) dismiss("backdrop_or_close");
        else setOpen(true);
      }}
    >
      <DialogContent
        data-testid="zylvie-offer-modal"
        className="bg-ink-800 border border-gold/30 text-white max-w-3xl w-[calc(100vw-2rem)] rounded-2xl p-0 overflow-hidden flex flex-col h-[92vh] max-h-[92vh]"
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-5 pb-3 flex-shrink-0 border-b border-white/5">
          <div className="flex items-center gap-3 mb-1">
            <span className="w-9 h-9 rounded-full border border-gold/40 flex items-center justify-center bg-ink-700">
              <Bowtie className="w-4 h-4 text-gold" />
            </span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-gold/85">
              Limited Offer
            </span>
          </div>
          <DialogTitle className="font-display text-xl md:text-2xl leading-tight">
            Before you browse — <span className="bk-gold-text italic">unlock this</span>
          </DialogTitle>

          <div className="mt-3 flex justify-center md:justify-start">
            <SessionUrgencyTimer
              storageKey="zylvie-offer-modal"
              label="This deal expires in"
              variant="compact"
            />
          </div>
        </DialogHeader>

        {/* Scrollable body — this scrolls, iframe is rendered at full content height */}
        <div
          className="flex-1 min-h-0 overflow-y-auto bg-white"
          data-testid="zylvie-frame-wrap"
        >
          <iframe
            ref={iframeRef}
            src={ZYLVIE_IFRAME}
            title="Bow Tie Kreative offer"
            data-testid="zylvie-iframe"
            className="zylvie-iframe block w-full"
            style={{
              border: 0,
              margin: 0,
              padding: 0,
              height: `${iframeHeight}px`,
              display: "block",
            }}
            scrolling="no"
            loading="eager"
          />
        </div>

        {/* Dismiss bar — always visible */}
        <div className="px-6 py-3 border-t border-white/10 bg-ink-900 flex items-center justify-center flex-shrink-0">
          <button
            onClick={() => dismiss("no_thanks_click")}
            data-testid="zylvie-dismiss"
            className="text-xs uppercase tracking-[0.22em] font-semibold text-white/65 hover:text-gold transition-colors py-2 px-2"
          >
            No thanks, let me see the website →
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
